import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Platform Operators — manage the cross-tenant operator accounts (tenantId null).
 * Platform-admin only. Backed by /platform/operators.
 */

interface Operator {
  id: string;
  email: string;
  fullName: string;
  role: string;
  active: boolean;
  createdAt: string;
}
interface CreateResult extends Operator {
  tempPassword: string;
}

const ROLE_OPTIONS = [
  { value: 'PlatformAdmin', label: 'Platform Admin', help: 'Full control across every tenant and platform config' },
  { value: 'PlatformUser', label: 'Platform User', help: 'Operator support — onboarding/help, no platform config or tenant deletion' },
];

const ROLE_LABEL: Record<string, string> = {
  PlatformAdmin: 'Platform Admin',
  PlatformUser: 'Platform User',
  Admin: 'Platform Admin', // legacy
};

export function PlatformOperatorsPage() {
  const { user } = useAuth();
  const [ops, setOps] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('PlatformUser');
  const [submitting, setSubmitting] = useState(false);
  const [justCreated, setJustCreated] = useState<CreateResult | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  async function load() {
    try {
      const data = await apiClient<Operator[]>('/platform/operators');
      setOps(data);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Failed to load operators');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setJustCreated(null);
    try {
      const result = await apiClient<CreateResult>('/platform/operators', {
        method: 'POST',
        body: { email: email.trim(), fullName: fullName.trim(), role },
      });
      setJustCreated(result);
      setEmail('');
      setFullName('');
      setRole('PlatformUser');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to create operator');
    } finally {
      setSubmitting(false);
    }
  }

  async function changeRole(o: Operator, next: string) {
    if (next === o.role) return;
    setRowBusy(o.id);
    setError('');
    try {
      await apiClient(`/platform/operators/${o.id}/role`, { method: 'PATCH', body: { role: next } });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to change role');
    } finally {
      setRowBusy(null);
    }
  }

  async function toggleActive(o: Operator) {
    setRowBusy(o.id);
    setError('');
    try {
      await apiClient(`/platform/operators/${o.id}/active`, { method: 'PATCH', body: { active: !o.active } });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to update operator');
    } finally {
      setRowBusy(null);
    }
  }

  const roleEditable = (o: Operator) => o.role === 'PlatformAdmin' || o.role === 'PlatformUser';

  return (
    <div>
      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>Operators</h1>
          <p style={styles.sub}>Platform staff with cross-tenant access.</p>
        </div>
        <span style={styles.count}>{ops.length} {ops.length === 1 ? 'operator' : 'operators'}</span>
      </div>

      {error && <div style={styles.error} onClick={() => setError('')}>{error}</div>}
      {justCreated && (
        <div style={styles.success}>
          <strong>Invited {justCreated.fullName}.</strong> They sign in with <code>{justCreated.email}</code>{' '}
          and temporary password <code>{justCreated.tempPassword}</code> — they’ll set their own on first login.{' '}
          <span style={styles.copyNote}>(Copy it now — it won’t be shown again.)</span>
        </div>
      )}

      <div style={styles.grid} className="ep-op-grid">
        <section style={styles.card}>
          {loading ? (
            <div style={styles.muted}>Loading operators…</div>
          ) : (
            <table style={styles.table} className="ep-op-table">
              <thead>
                <tr>
                  <th style={styles.th}>Name / Email</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.thAction} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {ops.map((o) => {
                  const isSelf = o.id === user?.sub;
                  return (
                    <tr key={o.id} style={o.active ? undefined : styles.inactiveRow}>
                      <td style={styles.td} data-label="Operator">
                        <div style={styles.name}>
                          {o.fullName}
                          {isSelf && <span style={styles.youPill}>you</span>}
                        </div>
                        <div style={styles.emailText}>{o.email}</div>
                      </td>
                      <td style={styles.td} data-label="Role">
                        {roleEditable(o) ? (
                          <select
                            value={o.role}
                            disabled={rowBusy === o.id}
                            onChange={(e) => changeRole(o, e.target.value)}
                            style={styles.roleSelect}
                            aria-label={`Role for ${o.fullName}`}
                          >
                            {ROLE_OPTIONS.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span style={styles.roleChip}>{ROLE_LABEL[o.role] || o.role}</span>
                        )}
                      </td>
                      <td style={styles.td} data-label="Status">
                        <span style={o.active ? styles.activePill : styles.inactivePill}>
                          {o.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={styles.tdAction} data-label="">
                        <button
                          style={o.active ? styles.deactivateBtn : styles.activateBtn}
                          onClick={() => toggleActive(o)}
                          disabled={rowBusy === o.id || isSelf}
                          title={isSelf ? 'You cannot deactivate your own account' : o.active ? 'Deactivate' : 'Reactivate'}
                        >
                          {o.active ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        <section style={{ ...styles.card, ...styles.formCard }} className="ep-op-form">
          <h2 style={styles.h2}>Invite an operator</h2>
          <form onSubmit={handleCreate}>
            <label style={styles.label}>Full name</label>
            <input style={styles.input} value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Operator" required />

            <label style={styles.label}>Email</label>
            <input style={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@yourco.com" required />

            <label style={styles.label}>Role</label>
            <div style={styles.roleRadios}>
              {ROLE_OPTIONS.map((r) => (
                <label key={r.value} style={{ ...styles.roleOption, ...(role === r.value ? styles.roleOptionOn : null) }}>
                  <input type="radio" name="role" value={r.value} checked={role === r.value} onChange={() => setRole(r.value)} style={{ marginRight: 8 }} />
                  <span>
                    <span style={styles.roleOptLabel}>{r.label}</span>
                    <span style={styles.roleOptHelp}>{r.help}</span>
                  </span>
                </label>
              ))}
            </div>

            <button type="submit" style={styles.submit} disabled={submitting}>
              {submitting ? 'Inviting…' : 'Send invite'}
            </button>
          </form>
        </section>
      </div>

      {/* Responsive rules: stack the form under the list, and collapse the
          table to cards on narrow screens. */}
      <style>{`
        @media (max-width: 820px) {
          .ep-op-grid { grid-template-columns: 1fr !important; }
          .ep-op-form { position: static !important; }
        }
        @media (max-width: 720px) {
          .ep-op-table thead { display: none; }
          .ep-op-table, .ep-op-table tbody, .ep-op-table tr, .ep-op-table td {
            display: block;
            width: 100%;
          }
          .ep-op-table tr {
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            margin-bottom: 12px;
            padding: 6px 4px;
          }
          .ep-op-table td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            text-align: right;
            padding: 8px 12px;
            border: none;
          }
          .ep-op-table td::before {
            content: attr(data-label);
            font-weight: 700;
            font-size: 0.72rem;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #6b7280;
            text-align: left;
          }
          .ep-op-table td[data-label=""]::before { content: ""; }
          .ep-op-table td[data-label=""] { justify-content: stretch; }
          .ep-op-table td[data-label=""] button { width: 100%; }
        }
      `}</style>
    </div>
  );
}

const ACCENT = '#4f46e5';
const ACCENT_SOFT = '#eef2ff';
const BORDER = '#e5e7eb';
const MUTED = '#6b7280';
const INK = '#0b1120';

const styles: Record<string, React.CSSProperties> = {
  headingRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18, gap: 12, flexWrap: 'wrap' },
  h1: { fontSize: '1.6rem', fontWeight: 800, margin: 0, color: INK },
  sub: { color: MUTED, fontSize: '0.9rem', margin: '4px 0 0' },
  count: { color: MUTED, fontSize: '0.85rem', paddingTop: 6, whiteSpace: 'nowrap' },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(300px, 1fr)', gap: 20, alignItems: 'start' },
  card: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  formCard: { position: 'sticky', top: 16 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  thAction: { width: 1, padding: '8px 10px', borderBottom: `2px solid ${BORDER}` },
  td: { padding: '12px 10px', borderBottom: '1px solid #f0f1f5', verticalAlign: 'top' },
  tdAction: { padding: '12px 10px', borderBottom: '1px solid #f0f1f5', textAlign: 'right', whiteSpace: 'nowrap' },
  inactiveRow: { opacity: 0.55 },
  name: { fontWeight: 700, color: INK, display: 'flex', alignItems: 'center', gap: 8 },
  youPill: { background: ACCENT_SOFT, color: ACCENT, borderRadius: 999, padding: '1px 8px', fontSize: '0.68rem', fontWeight: 700 },
  emailText: { color: MUTED, fontSize: '0.82rem' },
  roleChip: { display: 'inline-block', background: ACCENT_SOFT, color: ACCENT, borderRadius: 6, padding: '3px 10px', fontSize: '0.78rem', fontWeight: 700 },
  roleSelect: { padding: '6px 8px', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: '0.85rem', background: '#fff', cursor: 'pointer' },
  activePill: { background: '#dcfce7', color: '#166534', borderRadius: 999, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 },
  inactivePill: { background: '#f1f5f9', color: '#64748b', borderRadius: 999, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 },
  deactivateBtn: { background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 8, padding: '6px 12px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' },
  activateBtn: { background: '#059669', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' },
  h2: { fontSize: '1.05rem', fontWeight: 700, margin: '0 0 14px', color: INK },
  label: { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', margin: '12px 0 4px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: '0.9rem' },
  roleRadios: { display: 'flex', flexDirection: 'column', gap: 8 },
  roleOption: { display: 'flex', alignItems: 'flex-start', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 12px', cursor: 'pointer' },
  roleOptionOn: { borderColor: ACCENT, background: ACCENT_SOFT },
  roleOptLabel: { display: 'block', fontWeight: 700, fontSize: '0.86rem', color: INK },
  roleOptHelp: { display: 'block', fontSize: '0.76rem', color: MUTED, marginTop: 2 },
  submit: { width: '100%', marginTop: 16, padding: '11px 0', background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.92rem', fontWeight: 700, cursor: 'pointer' },
  muted: { color: MUTED, padding: '20px 4px', textAlign: 'center' },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#7f1d1d', borderRadius: 8, padding: '10px 14px', marginBottom: 16, cursor: 'pointer' },
  success: { background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#065f46', borderRadius: 8, padding: '10px 14px', marginBottom: 16, lineHeight: 1.5 },
  copyNote: { fontStyle: 'italic' },
};
